import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { z } from 'zod';
import { expenseInput, type ExpenseDto } from '@barbeiropro/contracts';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBranches } from '@/lib/org';
import { useCurrentRegister, useSaveExpense } from '../api';
import { EXPENSE_CATEGORIES } from '../labels';
import { handleFormError } from '../form-utils';
import { MoneyInput } from '../money-input';

type Values = z.input<typeof expenseInput>;
const GENERAL = 'general';

export function ExpenseModal({ expense, defaultBranchId, today, onClose }: { expense: ExpenseDto | 'new'; defaultBranchId: string | null; today: string; onClose: () => void }) {
  const editing = expense === 'new' ? null : expense;
  const branches = useBranches();
  const save = useSaveExpense();
  const [fromCash, setFromCash] = useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(expenseInput),
    defaultValues: {
      branchId: editing ? editing.branchId : defaultBranchId,
      category: editing?.category ?? '',
      description: editing?.description ?? '',
      amountCents: editing?.amountCents ?? 0,
      dueDate: editing?.dueDate.slice(0, 10) ?? today,
      paidAt: editing?.paidAt?.slice(0, 10) ?? null,
    },
  });
  const { errors } = form.formState;
  const branchId = form.watch('branchId') ?? null;
  const paidAt = form.watch('paidAt');
  const register = useCurrentRegister(branchId);
  const openRegister = register.data?.register ?? null;
  const canUseCash = !editing?.paidAt && !!paidAt && !!openRegister;

  useEffect(() => {
    if (!canUseCash) setFromCash(false);
  }, [canUseCash]);

  async function submit(values: Values) {
    try {
      await save.mutateAsync({
        id: editing?.id,
        body: { ...values, branchId: values.branchId ?? null, paidAt: values.paidAt || null, cashRegisterId: fromCash && openRegister ? openRegister.id : null } as never,
      });
      toast.success(editing ? 'Despesa atualizada.' : 'Despesa lançada.');
      onClose();
    } catch (e) {
      handleFormError(e, form);
    }
  }

  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title={editing ? 'Editar despesa' : 'Nova despesa'}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" form="expense-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar despesa'}
          </Button>
        </>
      }
    >
      <form id="expense-form" onSubmit={form.handleSubmit(submit)} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field id="exp-category" label="Categoria" error={errors.category?.message}>
          <Input id="exp-category" list="expense-categories" autoComplete="off" aria-invalid={!!errors.category} {...form.register('category')} />
          <datalist id="expense-categories">
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field id="exp-branch" label="Unidade">
          <Controller
            control={form.control}
            name="branchId"
            render={({ field }) => (
              <Select value={field.value ?? GENERAL} onValueChange={(v) => field.onChange(v === GENERAL ? null : v)}>
                <SelectTrigger id="exp-branch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GENERAL}>Geral (todas as unidades)</SelectItem>
                  {(branches.data ?? []).filter((b) => b.active).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Field id="exp-description" label="Descrição" error={errors.description?.message} className="sm:col-span-2">
          <Input id="exp-description" aria-invalid={!!errors.description} {...form.register('description')} />
        </Field>
        <Field id="exp-amount" label="Valor" error={errors.amountCents?.message}>
          <Controller control={form.control} name="amountCents" render={({ field }) => <MoneyInput id="exp-amount" value={field.value} onChange={field.onChange} aria-invalid={!!errors.amountCents} />} />
        </Field>
        <Field id="exp-due" label="Vencimento" error={errors.dueDate?.message}>
          <Input id="exp-due" type="date" aria-invalid={!!errors.dueDate} {...form.register('dueDate')} />
        </Field>
        <Field id="exp-paid" label="Pago em (opcional)" hint="Deixe em branco se ainda não foi paga." error={errors.paidAt?.message} className="sm:col-span-2 sm:max-w-xs">
          <Input id="exp-paid" type="date" aria-invalid={!!errors.paidAt} {...form.register('paidAt', { setValueAs: (v: string) => v || null })} />
        </Field>
        {paidAt && !editing?.paidAt && (
          <div className="flex items-start gap-2 sm:col-span-2">
            <Checkbox id="exp-cash" checked={fromCash} disabled={!canUseCash} onCheckedChange={(v) => setFromCash(v === true)} className="mt-0.5" />
            <div className="grid gap-0.5">
              <Label htmlFor="exp-cash">Retirar do caixa aberto</Label>
              <p className="text-xs text-muted-foreground">
                {!branchId
                  ? 'Escolha uma unidade para retirar do caixa.'
                  : openRegister
                    ? 'Registra uma saída em dinheiro no caixa aberto desta unidade.'
                    : 'Não há caixa aberto nesta unidade.'}
              </p>
            </div>
          </div>
        )}
      </form>
    </FormModal>
  );
}
